import {
	FileUploadErrorEvent,
	FileUploaderEvent,
} from '@volvo/vcdk/file-uploader';
import { useCallback, useState } from 'react';

import { emptyFilteredFiles } from 'library/components/controls/files/cropImage';
import {
	ALLOWED_FILE_EXTENSION_REGEX,
	MAX_FILE_LIMIT,
} from 'library/components/controls/files/fileUploadValidation';
import { validateFiles } from 'library/components/controls/files/functions';
import { useDialogState } from 'library/hooks/useDialogState';

import { FilteredList } from './Types';

export function useFileSelectionHandler() {
	const [filesToUpload, setFilesToUpload] = useState<File[]>([]);
	const [fileUploaderError, setFileUploaderError] = useState<string | null>(
		null
	);
	const [invalidFiles, setInvalidFiles] =
		useState<FilteredList>(emptyFilteredFiles);
	const {
		handleClose: closeWarningDialog,
		handleOpen: openWarningDialog,
		open: warningDialogOpen,
	} = useDialogState();

	const getFileKey = useCallback(
		(file: File): string =>
			`${file.name}:${file.size}:${file.lastModified}`,
		[]
	);

	const handleFilesSelected = useCallback(
		(event: FileUploaderEvent) => {
			if (event.type === 'vcdk-files-selected') {
				setFileUploaderError(null);
				const selectedFiles =
					event.detail.files?.map((f) => f.file).filter(Boolean) ??
					[];

				const validation = validateFiles(
					selectedFiles,
					filesToUpload,
					ALLOWED_FILE_EXTENSION_REGEX,
					MAX_FILE_LIMIT
				);

				const invalidKeys = new Set(
					validation
						? Array.from(validation.values()).flat().map(getFileKey)
						: []
				);
				const validFiles = validation
					? selectedFiles.filter(
							(file) => !invalidKeys.has(getFileKey(file))
						)
					: selectedFiles;

				if (validation) {
					setInvalidFiles(() => ({
						...emptyFilteredFiles,
						...Array.from(validation.entries()).reduce(
							(acc, [reason, files]) => {
								acc[reason] = files;
								return acc;
							},
							{} as FilteredList
						),
					}));
					openWarningDialog();
				}

				setFilesToUpload((prev) => {
					const byKey = new Map(
						prev.map((file) => [getFileKey(file), file])
					);

					for (const file of validFiles) {
						byKey.set(getFileKey(file), file);
					}
					return Array.from(byKey.values()).slice(0, MAX_FILE_LIMIT);
				});

				return;
			}

			if (event.type === 'vcdk-files-removed') {
				const removedFiles =
					event.detail.files?.map((f) => f.file).filter(Boolean) ??
					[];
				const removedKeys = new Set(removedFiles.map(getFileKey));

				setFilesToUpload((prev) =>
					prev.filter((f) => !removedKeys.has(getFileKey(f)))
				);
			}
		},
		[filesToUpload, getFileKey, openWarningDialog]
	);

	const handleFileUploaderError = useCallback(
		(event: FileUploadErrorEvent) => {
			const errorMessage = event.detail?.error;
			setFileUploaderError(
				typeof errorMessage === 'string' && errorMessage.length > 0
					? errorMessage
					: 'File upload error'
			);
		},
		[]
	);

	const clearFiles = useCallback(() => {
		setFilesToUpload([]);
		setFileUploaderError(null);
	}, []);

	const warningDialogClose = useCallback(() => {
		setInvalidFiles(emptyFilteredFiles);
		closeWarningDialog();
	}, [closeWarningDialog]);

	return {
		filesToUpload,
		fileUploaderError,
		setFileUploaderError,
		invalidFiles,
		warningDialogOpen,
		handleFilesSelected,
		handleFileUploaderError,
		clearFiles,
		warningDialogClose,
	};
}
